import { LitElement, html, nothing, type PropertyValues, type CSSResultGroup } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import styles from './styles.js';
import { emit } from '../utils/dom.utils.js';

// ── Types ────────────────────────────────────────────────────────────────────

export type DialogPosition =
  | 'center'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

// ══════════════════════════════════════════════════════════════════════════════
// uwc-dialog
// ══════════════════════════════════════════════════════════════════════════════

export class UwcDialog extends LitElement {
  static override styles: CSSResultGroup = [styles];

  // ── Public properties ────────────────────────────────────────────────────

  /** Controls dialog visibility. Reflect so `<uwc-dialog open>` works. */
  @property({ type: Boolean, reflect: true })
  open = false;

  /** Title text rendered in the default header. */
  @property()
  header = '';

  /** Show a dimming backdrop. Default: true. */
  @property({ type: Boolean, reflect: true })
  modal = true;

  /** Close on Escape key press. Default: true. */
  @property({ type: Boolean, attribute: 'close-on-escape' })
  closeOnEscape = true;

  /** Close when clicking the backdrop. Default: false. */
  @property({ type: Boolean, attribute: 'close-on-outside-click' })
  closeOnOutsideClick = false;

  /** Show X close button in the header. Default: true. */
  @property({ type: Boolean })
  closable = true;

  /** Allow dragging via the header. Default: false. */
  @property({ type: Boolean, reflect: true })
  override draggable = false;

  /** Show resize handle in bottom-right corner. Default: false. */
  @property({ type: Boolean, reflect: true })
  resizable = false;

  /** Show maximize / restore button in the header. Default: false. */
  @property({ type: Boolean })
  maximizable = false;

  /** Screen position of the dialog. Default: 'center'. */
  @property({ reflect: true })
  position: DialogPosition = 'center';

  /** CSS width value. Default: '50vw'. */
  @property()
  width = '50vw';

  /** CSS max-height value. Default: 'calc(100dvh - 2rem)'. */
  @property()
  height = '';

  // ── Internal state ───────────────────────────────────────────────────────

  @state() private _maximized = false;
  @state() private _hasFooter = false;
  @state() private _dragging  = false;

  @query('dialog') private _dialog!: HTMLDialogElement;

  // ── Lifecycle ────────────────────────────────────────────────────────────

  protected override updated(changed: PropertyValues): void {
    super.updated(changed);
    if (changed.has('open')) {
      if (this.open) {
        this._doOpen();
      } else {
        this._doClose();
      }
    }
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  private _doOpen(): void {
    const d = this._dialog;
    if (!d || d.open) return;
    // Clear positional inline overrides from a previous drag/resize/maximize
    // (do NOT use cssText='' — it wipes CSS custom properties too)
    d.style.top = '';
    d.style.right = '';
    d.style.bottom = '';
    d.style.left = '';
    d.style.margin = '';
    d.style.width = '';
    d.style.height = '';
    d.style.maxHeight = '';
    d.showModal();
    // Apply position via inline styles AFTER showModal() so we are in the top
    // layer. Inline styles beat the native <dialog> UA stylesheet (FIX 4 from
    // PlacementController). CSS classes alone cannot override UA styles here.
    this._applyPosition();
    emit(this, 'uwc-show');
    requestAnimationFrame(() => {
      const focusable = d.querySelector<HTMLElement>(
        'uwc-button:not([disabled]), uwc-inputtext, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    });
  }

  /**
   * Sets inline top/right/bottom/left/margin on the native <dialog> to achieve
   * the requested `position`. Inline styles have higher specificity than the
   * browser UA stylesheet that centres dialogs by default, so this reliably
   * overrides it regardless of which position variant is requested.
   */
  private _applyPosition(): void {
    const d = this._dialog;
    if (!d || this._maximized) return;

    const G = '2rem';

    // Each entry: [top, right, bottom, left, margin]
    const map: Record<string, [string, string, string, string, string]> = {
      //                top       right     bottom    left      margin
      'center':       [ '',       '',       '',       '',       ''        ],
      'top':          [ G,        '0',      'auto',   '0',      '0 auto'  ],
      'bottom':       [ 'auto',   '0',      G,        '0',      '0 auto'  ],
      'left':         [ '0',      'auto',   '0',      G,        'auto 0'  ],
      'right':        [ '0',      G,        '0',      'auto',   'auto 0'  ],
      'top-left':     [ G,        'auto',   'auto',   G,        '0'       ],
      'top-right':    [ G,        G,        'auto',   'auto',   '0'       ],
      'bottom-left':  [ 'auto',   'auto',   G,        G,        '0'       ],
      'bottom-right': [ 'auto',   G,        G,        'auto',   '0'       ],
    };

    const [top, right, bottom, left, margin] = map[this.position] ?? map['center'];
    d.style.top      = top;
    d.style.right    = right;
    d.style.bottom   = bottom;
    d.style.left     = left;
    d.style.margin   = margin;
    d.style.width    = this.width || '50vw';
    d.style.maxHeight = this.height || 'calc(100dvh - 2rem)';
  }

  private _doClose(): void {
    const d = this._dialog;
    if (!d || !d.open) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      this._finishClose(d);
      return;
    }
    d.classList.add('is-closing');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      d.classList.remove('is-closing');
      this._finishClose(d);
    };
    const onAnimEnd = (e: AnimationEvent) => {
      if (e.animationName === 'uwc-dlg-out') finish();
    };
    d.addEventListener('animationend', onAnimEnd);
    // Fallback: if animation doesn't fire, close after timeout
    setTimeout(() => {
      d.removeEventListener('animationend', onAnimEnd);
      finish();
    }, 220);
  }

  private _finishClose(d: HTMLDialogElement): void {
    if (d.open) d.close();
    if (this._maximized) this._maximized = false;
    emit(this, 'uwc-hide');
  }

  // ── Event handlers ───────────────────────────────────────────────────────

  private _onCancel(e: Event): void {
    e.preventDefault(); // suppress native Escape handling
    if (this.closeOnEscape) this.hide();
  }

  /** Close when the backdrop area of the <dialog> itself is clicked. */
  private _onDialogClick(e: MouseEvent): void {
    if (!this.closeOnOutsideClick) return;
    if ((e.target as Element) === this._dialog) this.hide();
  }

  private _onMaximize(): void {
    this._maximized = !this._maximized;
    emit(this, this._maximized ? 'uwc-maximize' : 'uwc-unmaximize');
    const d = this._dialog;
    if (this._maximized) {
      d.style.top          = '0';
      d.style.right        = '0';
      d.style.bottom       = '0';
      d.style.left         = '0';
      d.style.margin       = '0';
      d.style.width        = '100vw';
      d.style.maxWidth     = '100vw';
      d.style.height       = '100dvh';
      d.style.maxHeight    = '100dvh';
      d.style.borderRadius = '0';
    } else {
      // Restore: clear size/visual overrides then re-apply position
      d.style.width        = '';
      d.style.maxWidth     = '';
      d.style.height       = '';
      d.style.maxHeight    = '';
      d.style.borderRadius = '';
      this._applyPosition();
    }
  }

  private _onFooterSlotChange(e: Event): void {
    const slot = e.target as HTMLSlotElement;
    this._hasFooter = slot.assignedNodes({ flatten: true }).length > 0;
  }

  // ── Drag ─────────────────────────────────────────────────────────────────

  private _onHeaderMousedown(e: MouseEvent): void {
    if (!this.draggable || this._maximized) return;
    // Don't start drag when clicking a button inside the header
    if ((e.target as HTMLElement).closest('uwc-button')) return;
    e.preventDefault();

    const d      = this._dialog;
    const rect   = d.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const origL  = rect.left;
    const origT  = rect.top;

    this._dragging = true;
    emit(this, 'uwc-drag-start');

    const onMove = (ev: MouseEvent) => {
      const newL = Math.max(0, Math.min(origL + ev.clientX - startX, window.innerWidth  - d.offsetWidth));
      const newT = Math.max(0, Math.min(origT + ev.clientY - startY, window.innerHeight - d.offsetHeight));
      d.style.left   = `${newL}px`;
      d.style.top    = `${newT}px`;
      d.style.right  = 'auto';
      d.style.bottom = 'auto';
      d.style.margin = '0';
    };

    const onUp = () => {
      this._dragging = false;
      emit(this, 'uwc-drag-end');
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  }

  // ── Resize ────────────────────────────────────────────────────────────────

  private _onResizeMousedown(e: MouseEvent): void {
    if (!this.resizable || this._maximized) return;
    e.preventDefault();
    e.stopPropagation();

    const d      = this._dialog;
    const rect   = d.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = rect.width;
    const startH = rect.height;

    emit(this, 'uwc-resize-start');

    const onMove = (ev: MouseEvent) => {
      d.style.width  = `${Math.max(240, startW + ev.clientX - startX)}px`;
      d.style.height = `${Math.max(120, startH + ev.clientY - startY)}px`;
    };

    const onUp = () => {
      emit(this, 'uwc-resize-end');
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  }

  // ── Public API ────────────────────────────────────────────────────────────

  show()   { this.open = true;  }
  hide()   { this.open = false; }
  toggle() { this.open = !this.open; }

  // ── Render ────────────────────────────────────────────────────────────────

  protected override render() {
    const posClass = this._maximized
      ? 'uwc-dlg--maximized'
      : `uwc-dlg--${this.position}`;

    return html`
      <dialog
        part="dialog"
        class=${classMap({
          'uwc-dlg':            true,
          [posClass]:           true,
          'uwc-dlg--no-modal':  !this.modal,
          'uwc-dlg--draggable': this.draggable && !this._maximized,
          'uwc-dlg--resizable': this.resizable && !this._maximized,
          'uwc-dlg--dragging':  this._dragging,
        })}
        aria-labelledby="uwc-dlg-title"
        @cancel=${this._onCancel}
        @click=${this._onDialogClick}
      >
        <!-- ── Header ─────────────────────────────────────────────────── -->
        <div
          part="header"
          class="uwc-dlg__header"
          @mousedown=${this._onHeaderMousedown}
        >
          <slot name="header">
            <span id="uwc-dlg-title" part="title" class="uwc-dlg__title">
              ${this.header}
            </span>
          </slot>
          <div part="header-actions" class="uwc-dlg__actions">
            ${this.maximizable ? html`
              <uwc-button
                icon=${this._maximized ? 'fullscreen-exit' : 'fullscreen'}
                icon-only text variant="secondary" size="small"
                aria-label=${this._maximized ? 'Restore' : 'Maximize'}
                @uwc-click=${this._onMaximize}
              ></uwc-button>
            ` : nothing}
            ${this.closable ? html`
              <uwc-button
                icon="x-lg"
                icon-only text variant="secondary" size="small"
                aria-label="Close"
                @uwc-click=${() => this.hide()}
              ></uwc-button>
            ` : nothing}
          </div>
        </div>

        <!-- ── Content ────────────────────────────────────────────────── -->
        <div part="content" class="uwc-dlg__content">
          <slot></slot>
        </div>

        <!-- ── Footer ─────────────────────────────────────────────────── -->
        <div
          part="footer"
          class=${classMap({
            'uwc-dlg__footer':       true,
            'uwc-dlg__footer--hide': !this._hasFooter,
          })}
        >
          <slot name="footer" @slotchange=${this._onFooterSlotChange}></slot>
        </div>

        <!-- ── Resize handle ──────────────────────────────────────────── -->
        ${this.resizable && !this._maximized ? html`
          <div
            class="uwc-dlg__resize"
            aria-hidden="true"
            @mousedown=${this._onResizeMousedown}
          ></div>
        ` : nothing}
      </dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-dialog': UwcDialog; }
}
